import { Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Router, UNSAFE_LocationContext, createPath, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useMaintenance } from "../../contexts/MaintenanceContext";
import {
  FrameActivityContext,
  FramePortalContext,
  FrameTabIdContext,
  isDocsHref,
  isTabless,
  useTabs,
} from "../../contexts/TabsContext";
import { FrameEnvContext, PageLoader } from "../../AppRoutes";
import { AppLayout } from "./AppLayout";

// Keep-alive tab frames. The app shell (sidebar/header/tab strip) renders
// ONCE up here — switching tabs never repaints it. Each tab renders only the
// page content inside its own <Router> pinned to the tab's frozen location;
// inactive frames stay mounted but display:none, so nothing remounts or
// refetches on switch. Frames mount lazily (first activation) and remount
// when the tab's nonce is bumped by reloadTab.
//
// Two families of frame share one tab strip: ERP tabs live INSIDE AppLayout
// (sidebar + header); Docs tabs render FULL-SCREEN in their own DocsLayout,
// outside the shell. Whichever family owns the active tab is shown; the other
// is hidden but stays mounted (keep-alive). Tabless URLs (login, prints, bnb)
// render a normal live route tree while both families wait hidden.

const pathOf = (href) => (href || "").split("?")[0];

// Tracks real window resizes so a reactivated docs frame can tell "the window
// actually changed size while I was hidden" apart from "I was just switched
// to" — the latter needs no re-fit, so dispatching one on every switch was
// forcing Univer to redraw its canvas (a visible blink) on plain tab changes.
let lastWindowResizeAt = Date.now();
if (typeof window !== "undefined") {
  window.addEventListener("resize", () => {
    lastWindowResizeAt = Date.now();
  });
}

// Content-area spinner — the shell stays visible while a tab's page loads.
const FrameLoader = () => (
  <div className="flex items-center justify-center py-24">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const Frame = ({ tab, active, routes, isDocs = false }) => {
  const { navigateFrame } = useTabs();
  const [everActive, setEverActive] = useState(active);
  // Dialogs/sheets of this tab portal into this element (instead of body) so
  // they hide and reappear together with the frame.
  const [portalEl, setPortalEl] = useState(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    if (active && !everActive) setEverActive(true);
  }, [active, everActive]);

  // Univer renders to a <canvas> sized from its container. When a docs frame
  // is re-shown after the window was resized while it sat display:none, the
  // canvas can be stale — a resize tick makes Univer re-fit. Only fire that
  // when a resize genuinely happened since this frame was last shown, so a
  // plain tab switch doesn't force a needless (visibly flashy) canvas redraw.
  const syncedResizeAtRef = useRef(lastWindowResizeAt);
  useEffect(() => {
    if (!isDocs || !active) return undefined;
    if (lastWindowResizeAt <= syncedResizeAtRef.current) return undefined;
    const id = requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
      syncedResizeAtRef.current = lastWindowResizeAt;
    });
    return () => cancelAnimationFrame(id);
  }, [isDocs, active, tab.nonce]);

  // Router navigator for this frame: navigation from inside the tab updates
  // the tab's frozen location (and the browser URL when the tab is active).
  const navigator = useMemo(
    () => ({
      createHref: (to) => (typeof to === "string" ? to : createPath(to)),
      push: (to, state) => navigateFrame(tab.id, to, { state }),
      replace: (to, state) => navigateFrame(tab.id, to, { state, replace: true }),
      go: (n) => {
        if (activeRef.current) window.history.go(n);
      },
    }),
    [tab.id, navigateFrame]
  );

  if (!everActive) return null; // lazy: don't load restored tabs until first opened

  return (
    <div
      style={active ? undefined : { display: "none" }}
      // Pure-opacity fade: a transform-based animation would turn this div
      // into a containing block and misplace fixed dialog overlays inside.
      // Docs frames skip it here — DocsLayout's own <main> carries the fade
      // instead, so only the page content fades in, not the header/tab strip
      // (which stays static-feeling, same as the ERP shell).
      className={active && !isDocs ? "tab-fade-in" : undefined}
      data-testid="tab-frame"
      data-active={active}
    >
      <FrameActivityContext.Provider value={active}>
        <FrameTabIdContext.Provider value={tab.id}>
          <FramePortalContext.Provider value={portalEl}>
            {/* Reset the outer BrowserRouter's location context so this frame's
                Router mounts as if top-level (react-router forbids nesting). */}
            <UNSAFE_LocationContext.Provider value={null}>
              <Router key={tab.nonce || 0} location={tab.location} navigator={navigator}>
                <Suspense fallback={<FrameLoader />}>{routes}</Suspense>
              </Router>
            </UNSAFE_LocationContext.Provider>
          </FramePortalContext.Provider>
        </FrameTabIdContext.Provider>
      </FrameActivityContext.Provider>
      <div ref={setPortalEl} data-testid="tab-frame-portal" />
    </div>
  );
};

const TabFrames = ({ routes }) => {
  const { isAuthenticated } = useAuth();
  const { enabled: maintEnabled, isAllowed, loading: maintLoading } = useMaintenance();
  const { tabs, activeId } = useTabs();
  const location = useLocation();
  const tabless = isTabless(location.pathname);
  // Which family owns the active tab decides which shell is on screen.
  const activeTab = tabs.find((t) => t.id === activeId);
  const activeIsDocs = !!activeTab && isDocsHref(pathOf(activeTab.href));

  // Per-tab window scroll memory (window scroll is shared across frames).
  const scrollPos = useRef({});
  const activeIdRef = useRef(activeId);
  useEffect(() => {
    const onScroll = () => {
      scrollPos.current[activeIdRef.current] = window.scrollY;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useLayoutEffect(() => {
    if (activeIdRef.current !== activeId) {
      activeIdRef.current = activeId;
      window.scrollTo(0, scrollPos.current[activeId] ?? 0);
    }
  }, [activeId]);

  // Logged out or locked out by maintenance: no frames (a frame's route
  // guards would redirect-loop) — plain live routing handles login/lock.
  const locked = !maintLoading && maintEnabled && !isAllowed;
  if (!isAuthenticated || locked) {
    return <Suspense fallback={<PageLoader />}>{routes}</Suspense>;
  }

  // The ERP shell is hidden while a tabless live page OR a docs tab is on top.
  const shellHidden = tabless || activeIsDocs;
  const erpTabs = tabs.filter((t) => !isDocsHref(pathOf(t.href)));
  const docsTabs = tabs.filter((t) => isDocsHref(pathOf(t.href)));

  return (
    <>
      {/* ERP shell renders once; tab switches only swap the frames inside. */}
      <div style={shellHidden ? { display: "none" } : undefined}>
        <AppLayout>
          <FrameEnvContext.Provider value={true}>
            {erpTabs.map((tab) => (
              <Frame key={tab.id} tab={tab} active={!shellHidden && tab.id === activeId} routes={routes} />
            ))}
          </FrameEnvContext.Provider>
        </AppLayout>
      </div>

      {/* Docs tabs — full-screen keep-alive frames outside the ERP shell. Each
          renders its own DocsLayout; the shared tab strip lives in there. */}
      <FrameEnvContext.Provider value={true}>
        {docsTabs.map((tab) => (
          <Frame
            key={tab.id}
            tab={tab}
            isDocs
            active={!tabless && activeIsDocs && tab.id === activeId}
            routes={routes}
          />
        ))}
      </FrameEnvContext.Provider>

      {/* Prints, bnb, dashboard builder etc. render live; frames wait hidden. */}
      {tabless && <Suspense fallback={<PageLoader />}>{routes}</Suspense>}
    </>
  );
};

export default TabFrames;
